import {ensureRankersNamespace, RankersNamespaceKey} from "./rankers_namespace";

export const RankersUserAgentHeaderKey = 'RankersUserAgent';

// URL param key for transmitting user agent context through cached pages.
// See: plans/2026-01-24-request-context-for-js-components-via-url.md
export const USER_AGENT_PARAM_KEY = 'rankers-user-agent';

// Native app identifier pattern for extracting context from navigator.userAgent.
// The "campingnz" identifier is configured in config/models/native_apps.yml
// as `user_agent_identifier`. If adding new native apps, update this regex.
const NATIVE_APP_IDENTIFIER_PATTERN = /(campingnz)\/(iOS|Android)\/([\d.]+)/i;


/**
 * Extract native app context from browser user agent.
 * Matches format: "...browser UA... campingnz/iOS/2.23.2"
 *
 * This works when the native app configures its webview's custom user agent
 * to include the app identifier. See: plans/2026-01-26-native-app-webview-custom-user-agent.md
 *
 * @param {string} userAgent - The navigator.userAgent string
 * @returns {string|null} The native app identifier (e.g., "campingnz/iOS/2.23.2") or null
 */
function extractNativeAppContext(userAgent) {
    if (!userAgent) return null;
    const match = userAgent.match(NATIVE_APP_IDENTIFIER_PATTERN);
    return match ? `${match[1]}/${match[2]}/${match[3]}` : null;
}

/**
 * Retrieve the Rankers user agent context.
 *
 * When pages are cached, the server-rendered value in _head.html.erb may be
 * stale (from whoever first cached the page). The URL parameter and browser
 * user agent provide more reliable sources.
 *
 * Priority:
 * 1. URL parameter - explicit, survives page caching
 * 2. navigator.userAgent - runtime value from webview (if native app sets it)
 * 3. Global window - set by server, may be stale on cached pages
 *
 * @see plans/2026-01-24-request-context-for-js-components-via-url.md
 * @see plans/2026-01-26-native-app-webview-custom-user-agent.md
 * @returns {*|null}
 */
export function getRankersUserAgent() {
    // 1. Check URL parameter first - explicit declaration survives caching
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get(USER_AGENT_PARAM_KEY);
    if (fromUrl) return fromUrl;

    // 2. Check navigator.userAgent for native app context (runtime value from webview)
    const fromBrowser = extractNativeAppContext(navigator.userAgent);
    if (fromBrowser) return fromBrowser;

    // 3. Fallback to global namespace (set by server in _head.html.erb, may be stale)
    if (!window[RankersNamespaceKey]) return null;
    if (!window[RankersNamespaceKey][RankersUserAgentHeaderKey]) return null;
    return window[RankersNamespaceKey][RankersUserAgentHeaderKey];
}
/**
 * Set the Rankers user agent header value on the global window object.
 * This value is used by our JS libraries to set the 'X-Rankers-User-Agent' header on all requests.
 * @returns {*|null}
 */
export function setRankersUserAgent(userAgent) {
    ensureRankersNamespace();
    window[RankersNamespaceKey][RankersUserAgentHeaderKey] = userAgent;
    return window[RankersNamespaceKey][RankersUserAgentHeaderKey];
}
